import PencilIcon from '@heroicons/react/24/solid/PencilIcon';
import ChecklistIcon from '@mui/icons-material/Checklist';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { Button, Grid } from '@mui/material';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useTableColumnsProducto } from '@/shared';
import {
  SampleTable,
  SingleButton,
  SingleIconButton,
  TabTextLabel,
} from '@/shared/components/ui';
import { ScrollableDialogProps } from '@/shared/components/ui/Dialogs';
import { AlertSeverityEnum } from '@/shared/interfaces';
import { MaterialesKey, useMaterialesUtilizadosStore } from '@/store/erp';
import { useUiStore } from '@/store/ui';
import { EquiposUtilizadosOTTableType } from '../EquiposUtilizadosInstallOTTecAsig';
import CustomAutocompleteMultipleArrString from '@/shared/components/ui/CustomAutocompleteMultipleArrString';

export type ProductUbicacionSeriesModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  onChageKeyArrayStore?: MaterialesKey;
  enableAutoSelect?: boolean;
};
type SeriesFormData = string & {
  seriesArrString: string[];
};

const ProductUbicacionSeriesModal: React.FC<
  ProductUbicacionSeriesModalProps
> = ({
  open,
  setOpen,
  onChageKeyArrayStore = MaterialesKey.equipos_utilizados,
  enableAutoSelect = true,
}) => {
  ///* local state -----------------------
  const [isEdittingSeries, setIsEdittingSeries] = useState(false);
  const [isAutoSelectingSeries, setIsAutoSelectingSeries] = useState(false);

  ///* global state -----------------------
  const activeItemRow: EquiposUtilizadosOTTableType =
    useMaterialesUtilizadosStore(s => s.activeItemRow);
  const setActiveItemRow = useMaterialesUtilizadosStore(
    s => s.setActiveItemRow
  );
  const updateSelectedItemQuantity = useMaterialesUtilizadosStore(
    s => s.updateSelectedItemQuantity
  );
  const setAlert = useUiStore(s => s.setAlert);

  ///* form -----------------------
  const form = useForm<SeriesFormData>();
  const {
    formState: { errors },
  } = form;

  ///* table -----------------------
  const { columnsCargarSeries } = useTableColumnsProducto();

  ///* handlers -----------------------
  const onChangeSelectedSeries = useCallback(
    (value: any, item: any) => {
      updateSelectedItemQuantity(
        { ...item, pre_selected_series: value },
        onChageKeyArrayStore,
        'id'
      );
    },
    [updateSelectedItemQuantity]
  );
  const onChangeSavedSeries = useCallback(
    (value: any, item: any) => {
      updateSelectedItemQuantity(
        { ...item, selected_series: value },
        onChageKeyArrayStore,
        'id'
      );
    },
    [updateSelectedItemQuantity]
  );

  const onCloseAndClear = () => {
    setOpen(false);
    setActiveItemRow(null);
    setIsEdittingSeries(false);
    setIsAutoSelectingSeries(false);
    form.reset();
    form.setValue('seriesArrString', []);
  };

  return (
    <>
      <ScrollableDialogProps
        open={open}
        title={`Series del producto: ${activeItemRow?.producto_data?.codigo}`}
        width="70%"
        contentNode={
          useMaterialesUtilizadosStore.getState().activeItemRow?.selected_series
            ?.length > 0 && !isEdittingSeries ? (
            <>
              <Grid
                item
                xs={12}
                container
                spacing={1}
                mb={3}
                alignItems="center"
              >
                <Grid
                  item
                  container
                  xs={12}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Grid item md={6} xs={12}>
                    <TabTextLabel
                      text={`Series Seleccionadas: ${
                        useMaterialesUtilizadosStore.getState().activeItemRow
                          ?.pre_selected_series?.length || 0
                      }`}
                      pt="9px"
                      pl="0"
                      color="inherit"
                    />
                  </Grid>

                  <Grid item container md={6} xs={12} justifyContent="flex-end">
                    <SingleButton
                      newCustomButton
                      justifyContent="flex-end"
                      label="EDITAR SERIES"
                      variant="text"
                      color="info"
                      onClick={() => {
                        setIsEdittingSeries(true);
                        setIsAutoSelectingSeries(false);
                      }}
                      startIcon={<PencilIcon />}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <SampleTable
                    density="compact"
                    columns={columnsCargarSeries as any}
                    data={
                      // sobrevive la data xq se guarda en el table con el onChange
                      useMaterialesUtilizadosStore.getState().activeItemRow
                        ?.selected_series || []
                    }
                    enablePagination={true}
                  />
                </Grid>
              </Grid>
            </>
          ) : (
            <>
              <Grid
                item
                xs={12}
                container
                // spacing={1}
                mb={3}
                alignItems="center"
              >
                <Grid item md={6} xs={12}>
                  <TabTextLabel
                    text={`Series Disponibles xD: ${
                      useMaterialesUtilizadosStore.getState().activeItemRow
                        ?.series?.length || 0
                    }`}
                    pt="9px"
                    pl="0"
                    pb="0"
                    color="inherit"
                  />
                </Grid>

                <Grid item md={6} xs={12}>
                  <TabTextLabel
                    text={`Series Seleccionadas: ${
                      useMaterialesUtilizadosStore.getState().activeItemRow
                        ?.pre_selected_series?.length || 0
                    }`}
                    pl="0"
                    color="inherit"
                    pb="0"
                  />
                </Grid>

                <Grid
                  item
                  container
                  xs={12}
                  alignItems="center"
                  justifyContent="flex-start"
                  spacing={1}
                >
                  <Grid item xs={12} md={6}>
                    <TabTextLabel
                      text={`Series Requeridas: ${
                        (
                          useMaterialesUtilizadosStore.getState()
                            .activeItemRow as EquiposUtilizadosOTTableType
                        )?.cantidad || 0
                      }`}
                      pl="0"
                      pb="0"
                      color="inherit"
                    />
                  </Grid>

                  {/* ----- Autoselection ----- */}
                  {enableAutoSelect && (
                    <Grid
                      item
                      xs={12}
                      md={6}
                      container
                      justifyContent="flex-start"
                    >
                      <Grid item>
                        <SingleButton
                          newCustomButton
                          justifyContent="flex-start"
                          label="SELECCIÓN AUTOMÁTICA"
                          variant="text"
                          color="info"
                          disabled={isAutoSelectingSeries}
                          onClick={() => {
                            setIsAutoSelectingSeries(true);

                            const serieArr = useMaterialesUtilizadosStore
                              .getState()
                              .activeItemRow?.series?.slice(
                                0,
                                useMaterialesUtilizadosStore.getState()
                                  .activeItemRow?.cantidad
                              );

                            setActiveItemRow({
                              ...useMaterialesUtilizadosStore.getState()
                                .activeItemRow,
                              selected_series: serieArr,
                              pre_selected_series: serieArr,
                            });

                            // set selected series table data
                            onChangeSelectedSeries(serieArr, activeItemRow);
                          }}
                          startIcon={<ChecklistIcon />}
                        />
                      </Grid>
                      <Grid item justifyContent="flex-start">
                        {isAutoSelectingSeries && (
                          <SingleIconButton
                            startIcon={<FilterAltOffIcon />}
                            onClick={() => {
                              setIsAutoSelectingSeries(false);
                              // clear auto selected series
                              setActiveItemRow({
                                ...useMaterialesUtilizadosStore.getState()
                                  .activeItemRow,
                                selected_series: [],
                              });

                              // set selected series table data
                              onChangeSelectedSeries([], activeItemRow);
                            }}
                          />
                        )}
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>

              {
                // =============================================================
                // Cuando se clickea botón de selección automática
                // =============================================================
                isAutoSelectingSeries?
                <Grid item justifyContent="flex-start">
                  <p>Las series fueron seleccionadas automaticamente.</p>
                </Grid>
                :
                // =============================================================
                // Check Box List de series de manera manual 
                // =============================================================
                <CustomAutocompleteMultipleArrString
                  label="Series Disponibles"
                  name="seriesArrString"
                  textFieldKey="seriesArrString"
                  control={form.control as any}
                  // options
                  defaultValue={
                    isEdittingSeries
                      ? useMaterialesUtilizadosStore.getState().activeItemRow
                          ?.savedSeries
                      : []
                  }
                  options={activeItemRow?.series || []}
                  isLoadingData={false}
                  // errors
                  error={!!errors.seriesArrString?.length}
                  helperText={errors.seriesArrString?.message}
                  // disabled
                  disabled={isAutoSelectingSeries}
                  // raw string[] ---
                  enableStringify={false}
                  onChangeValue={value => {
                    const seriesArr = (value as string[]) || [];
                    const seriesLength = seriesArr.length;
                    if (seriesLength > (activeItemRow?.cantidad as number)) {
                      setAlert(
                        true,
                        `La cantidad de series seleccionadas no puede ser MAYOR a la cantidad utilizada: ${activeItemRow?.cantidad}`,
                        AlertSeverityEnum.error
                      );
                      // return;
                    }

                    setActiveItemRow({
                      ...useMaterialesUtilizadosStore.getState().activeItemRow,
                      pre_selected_series: seriesArr,
                    });

                    // set selected series table data
                    onChangeSelectedSeries(seriesArr, activeItemRow);
                  }}
                />
              }
            </>
          )
        }
        // actions
        showCustomButtons
        // =====================================================================
        // botones para selección automática de 
        // =====================================================================
        customButtons={
          <>
            <Grid
              item
              container
              xs={12}
              alignItems="center"
              justifyContent="flex-end"
            >
              <Button
                onClick={() => {
                  setOpen(false);
                  setActiveItemRow(null);
                  setIsEdittingSeries(false);
                }}
                variant="text"
              >
                Cerrar
              </Button>

              {isEdittingSeries ||
              !!!useMaterialesUtilizadosStore.getState().activeItemRow
                ?.savedSeries?.length ? (
                <Button
                  onClick={() => {
                    ////* validations
                    // const seriesLength = selectedProductSeries.length;
                    const preSelectedSeriesLength =
                      useMaterialesUtilizadosStore.getState().activeItemRow
                        ?.pre_selected_series?.length;

                    if (
                      +(activeItemRow?.cantidad as number || 0) !== +preSelectedSeriesLength
                    ) {
                      setAlert(
                        true,
                        `La cantidad de series seleccionadas no puede ser DISTINTA a la cantidad utilizada: ${activeItemRow?.cantidad}`,
                        AlertSeverityEnum.error
                      );
                      return;
                    }

                    // set saved series
                    onChangeSavedSeries(
                      useMaterialesUtilizadosStore.getState().activeItemRow
                        ?.pre_selected_series,
                      useMaterialesUtilizadosStore.getState().activeItemRow
                    );
                    setActiveItemRow({
                      ...useMaterialesUtilizadosStore.getState().activeItemRow,
                      selected_series:
                        useMaterialesUtilizadosStore.getState().activeItemRow
                          ?.pre_selected_series,
                      pre_selected_series: [],
                    });

                    // close and clear
                    onCloseAndClear();
                  }}
                  variant="outlined"
                >
                  Guardar Series xD
                </Button>
              ) : null}
            </Grid>
          </>
        }
        // --------------- normal actions
        // onConfirm={() => {

        //   const preSelectedSeriesLength =
        //     useMaterialesUtilizadosStore.getState().activeItemRow
        //       ?.pre_selected_series?.length;

        //   if (+(activeItemRow?.cantidad || 0) !== +preSelectedSeriesLength) {
        //     setAlert(
        //       true,
        //       `La cantidad de series seleccionadas no puede ser DISTINTA a la cantidad utilizada: ${activeItemRow?.cantidad}`,
        //       AlertSeverityEnum.error
        //     );
        //     return;
        //   }

        //   // set saved series
        //   onChangeSavedSeries(
        //     useMaterialesUtilizadosStore.getState().activeItemRow
        //       ?.pre_selected_series,
        //     useMaterialesUtilizadosStore.getState().activeItemRow
        //   );
        //   setActiveItemRow({
        //     ...useMaterialesUtilizadosStore.getState().activeItemRow,
        //     selected_series:
        //       useMaterialesUtilizadosStore.getState().activeItemRow
        //         ?.pre_selected_series,
        //     pre_selected_series: [],
        //   });

        //   // close and clear
        //   onCloseAndClear();
        // }}
        onClose={() => {
          onCloseAndClear();
        }}
        // cancelTextBtn="Cerrar"
        // confirmTextBtn="Guardar Series"
      />
    </>
  );
};

export default ProductUbicacionSeriesModal;
